import React, { useContext, useEffect, useReducer, useRef } from 'react';
import classNames from 'classnames';
import { produce } from 'immer';
import { pick } from 'lodash';
import { Validator, validator, Rules } from '../index';
import { Container } from './styled';
import LockIcon from './LockIcon';

export type Styles = {
  width?: number | string;
  height?: number | string;
  fontSize?: number;
  radius?: number;
};

export interface Props {
  type?: string;
  min?: string;
  max?: string;
  maxLength?: number;
  className?: string;
  innerRef?: React.RefObject<HTMLInputElement> | null;
  // 발리데이터
  rules?: Rules[];
  validType?: 'realtime' | 'blur';
  error?: string | string[] | null;
  styles?: Styles;
  value?: string | number;
  // ...rest #################
  disabled?: boolean;
  placeholder?: string | undefined;
  readonly?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, error?: boolean) => void;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  keyEnter?: (value: string) => void;
  onClick?: () => void;
}

export type Action = { type: 'type'; value: string } | { type: 'error'; value: string | string[] };

export interface State {
  error: string | string[];
  type: string;
  mount: boolean;
}

const initialState: State = { error: '', type: 'text', mount: false };
// prettier-ignore
const reducer = (state: State, action: Action): State => {
  return produce(state, (draft: State): void => {
    switch(action.type) {
      case 'error': draft['error'] = action.value; break;
      case 'type': draft['type'] = action.value; break;
    }
  });
};

const Input: React.FC<Props> = (props): JSX.Element => {
  const { onChange = () => false, onBlur = () => false, keyEnter = () => false } = props;

  const ref = useRef<HTMLInputElement>(null);
  const inputRef = props.innerRef ?? ref;
  const value = (props.value ?? (inputRef.current?.value || '')).toString();
  const valid = { realtime: props.validType === 'realtime', blur: props.validType === 'blur' };

  const initialize = (init: State) => ({ ...init, error: props.error ?? '', type: props.type ?? 'text' });
  const [state, setState] = useReducer(reducer, initialState, initialize);

  const validatorState = useContext(Validator.Context);
  const { excute } = validatorState;

  // Error 리셋
  useEffect(() => {
    if (props.error) setState({ type: 'error', value: props.error });
    if (state.error && !props.error) setState({ type: 'error', value: '' });
  }, [props.error]);

  // value 값 변경시 error리셋
  useEffect(() => {
    if (!valid.realtime && !!state.error && !props.error) setState({ type: 'error', value: '' });
  }, [value]);

  // 비활성화시 valid error 리셋
  useEffect(() => {
    if (props.disabled) setState({ type: 'error', value: '' });
  }, [props.disabled]);

  // EXCUTE EFFECT #############################################
  useEffect(() => {
    if (excute && props.rules && !props.disabled) {
      const { validError } = validator.excute(props.rules, value);
      setState({ type: 'error', value: validError });
    }
  }, [excute]);

  const onKeyPressHandler = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') keyEnter(value);
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    // REAL TIME VALID EFFECT #############################################
    if (valid.realtime && props.rules) {
      const excludeRequired = props.rules.filter((el) => el !== 'required');
      const { validError } = validator.excute(excludeRequired, e.target.value);
      setState({ type: 'error', value: validError });
      onChange(e, !!validError);
    } // 리얼타임이 아닐시 에러가 없으면 error 초기화
    if (!valid.realtime) {
      if (!excute && state.error && !props.error) {
        onChange(e);
        setState({ type: 'error', value: '' });
      } else {
        onChange(e);
      }
    }
  };

  const onBlurHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (valid.blur && props.rules && !props.disabled) {
      const { validError } = validator.excute(props.rules, e.target.value);
      setState({ type: 'error', value: validError });
    }
    onBlur(e);
  };

  const className = classNames('jack__input__container', {
    [props.className as string]: props.className,
    error: state.error.length,
    readonly: props.readonly,
    disabled: props.disabled,
    value: value.length ? 'active' : undefined,
  });

  return (
    <Container {...props.styles} className={className}>
      <input
        ref={inputRef}
        value={props.value}
        type={state.type}
        spellCheck="false"
        className="jack__input"
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        onKeyPress={onKeyPressHandler}
        readOnly={props.readonly}
        {...pick(props, ['placeholder', 'disabled', 'min', 'max', 'maxLength', 'onFocus'])}
      />
      {props.type === 'password' && <LockIcon setState={setState} type={state.type} disabled={props.disabled} />}
      <Validator.Error error={state.error} top={props.styles?.height ?? 40} />
    </Container>
  );
};

export default Input;
